import type {ReactElement} from 'react';
import {useContext} from 'react';
import {Icon} from '@Components/icon-v2/icon';
import {IconShape, IconSize, IconType} from '@Components/icon-v2/icon.types';
import {isUserLoggedIn} from '@Libraries/user.library';
import {emailMakerContext} from '@EmailMakerReact/email-maker-react.context';
import {EditorViewport} from '@EmailMakerReact/email-maker-react.types';
import {useEmailMakerUndoRedoState} from '@EmailMakerReact/hooks/use-email-maker-undo-redo-state';
import {useIsInVersionHistoryMode} from '@EmailMakerReact/hooks/use-is-in-version-history-mode';
import {EmailMakerEmbeddedNav} from './components/email-maker-embedded-nav/email-maker-embedded-nav';
import {EmailMakerNavLogo} from './components/email-maker-nav-logo/email-maker-nav-logo';
import {EmailMakerVersionHistoryNav} from './components/email-maker-version-history-nav/email-maker-version-history-nav';
import {NavActions} from './components/nav-actions/nav-actions';
import type {NavByModeParams} from './email-maker-nav.types';
import styles from './email-maker-nav.module.scss';

/** Clears the content-planner return-cache key before navigating back, so the planner does not stale-redirect on the next visit. */
function handleBackClick(): void {
  if (isUserLoggedIn() && window.PMW.redirectUrlCache.getCache(window.PMW.redirectUrlCache.CONTENT_PLANNER_RETURN_CACHE_KEY)) {
    window.PMW.redirectUrlCache.deleteCache(window.PMW.redirectUrlCache.CONTENT_PLANNER_RETURN_CACHE_KEY);
  }
  window.history.back();
}

function renderBackButton(editorViewport: EditorViewport): ReactElement | null {
  if (editorViewport === EditorViewport.MOBILE) {
    return null;
  }
  return (
    <Icon
      icon="icon-arrow-left"
      onClick={handleBackClick}
      isUsedAsButton
      ariaLabel={window.i18next.t('pmwjs_back')}
      shape={IconShape.SQUARE}
      type={IconType.DARK_GHOST}
      size={IconSize.SIZE_ICON_20}
      className={styles.backButton}
    />
  );
}

function renderNavByMode({editor, actions, state, canUndo, canRedo, store}: NavByModeParams): ReactElement {
  if (store.isEmbedded) {
    return <EmailMakerEmbeddedNav editor={editor} actions={actions} canUndo={canUndo} canRedo={canRedo} store={store} />;
  }

  const {editorViewport} = state;

  return (
    <header className={styles.navWrapper}>
      <nav className={styles.navbar} aria-label={window.i18next.t('pmwjs_email_maker_nav_aria')}>
        <div className={styles.left}>
          {renderBackButton(editorViewport)}
          <EmailMakerNavLogo editorViewport={editorViewport} />
        </div>

        <NavActions
          isEditorLoading={state.isEditorLoading}
          editorViewport={editorViewport}
          editor={editor}
          actions={actions}
          canUndo={canUndo}
          canRedo={canRedo}
          saveError={state.saveError}
        />
      </nav>
    </header>
  );
}

export function EmailMakerNav(): ReactElement {
  const {state, actions, editor, store} = useContext(emailMakerContext);
  const {canUndo, canRedo} = useEmailMakerUndoRedoState(editor);
  const isInVersionHistoryMode = useIsInVersionHistoryMode();

  if (isInVersionHistoryMode) {
    return <EmailMakerVersionHistoryNav />;
  }

  return renderNavByMode({editor, actions, state, store, canUndo, canRedo});
}
